import React, { useState, useEffect, useContext, useRef } from "react";
import { DatePicker, Select } from "antd";
import { RENTABIKE } from "./rentabikeConstants";
import "./RentabikeFilter.css";
import { VILLAGES_OBJ } from "../../constants/constants";
import { FIELD_WIDTHS } from "../../config/layoutConfig";
// remover moment (apenas para testes com dados pré-preenchidos)
import moment from "moment";
import ClearStateBtn from "../../components/ClearStateBtn/ClearStateBtn";
import { AppContext } from "../../context";
import { searchBikes } from "./searchBikes";
import { returnAvailableTimes } from "./library/returnAvailbleTimes";

function RentabikeFilter({ setProducts }) {
  const info = useContext(AppContext);
  const [rentabike, setRentabike] = useState({});
  const [bikeOptions, setBikeOptions] = useState([]);
  const [pickupLocations, setPickupLocations] = useState(null);
  const [returnLocations, setReturnLocations] = useState(null);

  const previousState = useRef(null);

  // set values stored in info.rentabike
  useEffect(() => {
    if (info.rentabike) {
      setRentabike({
        category: info?.rentabike?.category,
        pickup_loc: info?.rentabike?.pickup_loc,
        pickup_date: info?.rentabike?.pickup_date,
        pickup_time: info?.rentabike?.pickup_time,
        return_loc: info?.rentabike?.return_loc,
        return_date: info?.rentabike?.return_date,
        return_time: info?.rentabike?.return_time,
      });
    }
    // }
  }, []);

  useEffect(() => {
    (async () => {
      let bikes = await searchBikes(
        rentabike.category,
        rentabike.pickup_loc,
        rentabike.return_loc
      );

      // make bikes available if pickup and return dates and times are set
      if (
        rentabike.pickup_loc &&
        rentabike.pickup_date &&
        rentabike.pickup_time &&
        rentabike.return_loc &&
        rentabike.return_date &&
        rentabike.return_time
      ) {
        bikes = bikes.map((v) => ({
          ...v,
          status: "available",
          gen_price:
            v.gen_price *
            // if 0 days (same day return), return 1 day
            (rentabike.return_date.diff(rentabike.pickup_date, "days") || 1),
        }));
      }

      setProducts(bikes);
      // only updates bike list if the change was not in bike selection
      if (
        previousState?.current?.pickup_loc !== rentabike.pickup_loc ||
        previousState?.current?.return_loc !== rentabike.return_loc ||
        (!rentabike.pickup_loc && !rentabike.return_loc && !rentabike.category)
      ) {
        const bikeOptionCategories = bikes?.map((v) => v.ren_category);
        const uniqueBikeOptions = [...new Set(bikeOptionCategories)].map(
          (v) => ({
            value: v,
            label: info.t(v),
          })
        );
        setBikeOptions(uniqueBikeOptions);
      }

      let pickupLocations = [];
      let returnLocations = [];
      if (bikes?.length) {
        bikes.forEach((v) => {
          v.ren_pickup_locs.forEach((l) => pickupLocations.push(l));
          v.ren_return_locs.forEach((r) => returnLocations.push(r));
        });

        // unique return locations
        const uniquePickupLocations = [...new Set(pickupLocations)].map(
          (l) => ({ value: l, label: VILLAGES_OBJ[l] })
        );

        setPickupLocations(uniquePickupLocations);

        // unique return locations
        const uniqueReturnLocations = [...new Set(returnLocations)].map(
          (l) => ({ value: l, label: VILLAGES_OBJ[l] })
        );

        setReturnLocations(uniqueReturnLocations);
      }
      previousState.current = {
        category: rentabike.category,
        pickup_loc: rentabike.pickup_loc,
        return_loc: rentabike.return_loc,
      };
    })();
  }, [rentabike]);

  return (
    <div style={{ marginTop: 10 }}>
      <div className="ahp-rentabike-category">
        {info.t("bike")}
        <Select
          placeholder=""
          options={bikeOptions}
          value={rentabike.category || info.rentabike?.category}
          onChange={(value) => {
            setRentabike({ ...rentabike, category: value });
            info.setRentabike({ ...info.rentabike, category: value });
          }}
          allowClear
          style={{ width: FIELD_WIDTHS.L }}
          bordered={false}
        />
      </div>

      <div className="ahp-rentabike-pickup">
        {info.t("pickup")}
        <Select
          placeholder={info.t("pickupAt")}
          options={pickupLocations}
          style={{ width: FIELD_WIDTHS.XL }}
          value={rentabike.pickup_loc || info.rentabike?.pickup_loc}
          onChange={(value) => {
            setRentabike({ ...rentabike, pickup_loc: value });
            info.setRentabike({ ...info.rentabike, pickup_loc: value });
          }}
          allowClear
          bordered={false}
        />
        <DatePicker
          placeholder={info.t("date")}
          value={rentabike.pickup_date || info.rentabike?.pickup_date}
          onChange={(value) => {
            setRentabike({ ...rentabike, pickup_date: value });
            info.setRentabike({ ...info.rentabike, pickup_date: value });
            if (value > rentabike.return_date) {
              setRentabike({ ...rentabike, return_date: undefined });
              info.setRentabike({ ...info.rentabike, return_date: undefined });
            }
          }}
          showToday={false}
          bordered={false}
        />
        <Select
          options={RENTABIKE.AVAILABLE_TIME.map((t) => ({
            value: t,
            label: t,
          }))}
          placeholder={info.t("time")}
          value={rentabike.pickup_time || info.rentabike?.pickup_time}
          onChange={(value) => {
            if (
              rentabike.pickup_date &&
              rentabike.pickup_date === rentabike.return_date &&
              value > rentabike.return_time
            ) {
              setRentabike({
                ...rentabike,
                pickup_time: value,
                return_time: undefined,
              });
              info.setRentabike({
                ...info.rentabike,
                pickup_time: value,
                return_time: undefined,
              });
            } else {
              setRentabike({ ...rentabike, pickup_time: value });
              info.setRentabike({ ...info.rentabike, pickup_time: value });
            }
          }}
          allowClear
          style={{ width: FIELD_WIDTHS.S }}
          bordered={false}
        />
      </div>

      <div className="ahp-rentabike-return">
        {info.t("return")}
        <Select
          placeholder={info.t("returnAt")}
          options={returnLocations}
          value={rentabike.return_loc || info.rentabike?.return_loc}
          onChange={(value) => {
            setRentabike({ ...rentabike, return_loc: value });
            info.setRentabike({ ...info.rentabike, return_loc: value });
          }}
          style={{ width: FIELD_WIDTHS.XL }}
          allowClear
          bordered={false}
        />
        <DatePicker
          placeholder={info.t("date")}
          disabledDate={(date) => {
            const isLaterThan = date < rentabike.pickup_date;
            return isLaterThan;
          }}
          value={rentabike.return_date || info.rentabike?.return_date}
          onChange={(value) => {
            setRentabike({ ...rentabike, return_date: value });
            info.setRentabike({ ...info.rentabike, return_date: value });
          }}
          showToday={false}
          bordered={false}
        />
        <Select
          options={returnAvailableTimes(
            rentabike.pickup_date,
            rentabike.pickup_time,
            rentabike.return_date
          )}
          placeholder={info.t("time")}
          value={rentabike.return_time || info.rentabike?.return_time}
          onChange={(value) => {
            setRentabike({ ...rentabike, return_time: value });
            info.setRentabike({ ...info.rentabike, return_time: value });
          }}
          allowClear
          style={{ width: FIELD_WIDTHS.S }}
          bordered={false}
        />
      </div>

      <ClearStateBtn
        text={info.t("clearFields")}
        stateAndSetStateArray={[
          { state: info.rentabike, setState: info.setRentabike },
          { state: rentabike, setState: setRentabike },
        ]}
      />
    </div>
  );
}

export default RentabikeFilter;
